import {
  Box,
  Button,
  FormControl,
  FormLabel,
  HStack,
  Input,
  Link,
  Select,
  Text,
  VStack,
} from "@chakra-ui/react";
import { useRef, useState } from "react";
import { RiUploadLine } from "react-icons/ri";
import { ResizableDrawer } from "src/components/Layout/ResizableDrawer";
import { UploadFileItem } from "src/components/UploadButtons/UploadFileQuestion/UploadFileItem";
import * as FSQ from "src/generated/hooks/form-service/queries";
import { useAvelaToast } from "src/hooks/useAvelaToast";
import { useOrganization } from "src/hooks/useOrganization";
import {
  generateFeatureNames,
  processFeatureCollectionForImport,
  transformToFeatures,
} from "./utils/boundaryUtils";
import { handleGeoJSONFileUpload } from "./utils/fileUploadHandler";
import type { GeoJSONData, GeoJSONType } from "./utils/types";
import { GeoJSONFeature, GeoJSONFeatureCollection } from "./utils/types";

interface ImportBoundariesDrawerProps {
  onBoundaryImported: () => void;
  isOpen: boolean;
  onClose: () => void;
}

export const ImportBoundariesDrawer = ({
  onBoundaryImported,
  isOpen,
  onClose,
}: ImportBoundariesDrawerProps) => {
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [uploadStatus, setUploadStatus] = useState<
    "uploading" | "success" | "error" | null
  >(null);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [geoJsonData, setGeoJsonData] = useState<GeoJSONData | null>(null);
  const [availablePropertyKeys, setAvailablePropertyKeys] = useState<string[]>(
    []
  );
  const [selectedNameKey, setSelectedNameKey] = useState<string>("");
  const [geoJsonType, setGeoJsonType] = useState<GeoJSONType | null>(null);
  const [boundaryName, setBoundaryName] = useState<string>("");
  const fileInputRef = useRef<HTMLInputElement>(null);
  const toast = useAvelaToast();
  const organization = useOrganization();

  const postBoundaryMutation = FSQ.usePostBoundaries();

  const handleFileUpload = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setUploadedFile(file);
    setUploadStatus("uploading");
    setUploadError(null);

    const result = await handleGeoJSONFileUpload(file);

    if (result.isValid && result.geoJsonData && result.geoJsonType) {
      setGeoJsonData(result.geoJsonData);
      setGeoJsonType(result.geoJsonType);
      setUploadError(null);
      setAvailablePropertyKeys(result.propertyKeys);

      if (result.geoJsonType === "FeatureCollection") {
        if (result.propertyKeys.length > 0) {
          setSelectedNameKey(result.propertyKeys[0] || "");
        }
      } else if (result.geoJsonType === "Feature") {
        if (result.propertyKeys.length > 0) {
          setSelectedNameKey(result.propertyKeys[0] || "");
        }
        const feature = result.features[0];
        setBoundaryName((feature?.properties?.name as string) || "");
      } else if (result.geoJsonType === "Geometry") {
        setBoundaryName("");
      }

      setUploadStatus("success");
    } else {
      setUploadStatus("error");
      setUploadError(result.error || "Invalid GeoJSON file");
    }
  };

  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  const handleRemoveFile = () => {
    setUploadedFile(null);
    setUploadStatus(null);
    setUploadError(null);
    setGeoJsonData(null);
    setGeoJsonType(null);
    setAvailablePropertyKeys([]);
    setSelectedNameKey("");
    setBoundaryName("");
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleCancel = () => {
    setUploadedFile(null);
    setUploadStatus(null);
    setUploadError(null);
    setGeoJsonData(null);
    setGeoJsonType(null);
    setAvailablePropertyKeys([]);
    setSelectedNameKey("");
    setBoundaryName("");
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
    onClose();
  };

  const handleImport = async () => {
    if (!geoJsonData || !geoJsonType) return;

    let processedGeoJsonData: GeoJSONFeatureCollection;

    if (geoJsonType === "FeatureCollection") {
      if (!selectedNameKey) return;
      const featureCollection = geoJsonData as GeoJSONFeatureCollection;

      const processedFeatures = processFeatureCollectionForImport(
        featureCollection.features,
        selectedNameKey
      );

      processedGeoJsonData = {
        type: "FeatureCollection",
        features: processedFeatures,
      };
    } else if (geoJsonType === "Feature") {
      const feature = geoJsonData as GeoJSONFeature;
      processedGeoJsonData = {
        type: "FeatureCollection",
        features: [
          {
            ...feature,
            properties: {
              ...feature.properties,
              name: boundaryName || feature.properties?.name || "Boundary",
            },
          },
        ],
      };
    } else if (geoJsonType === "Geometry") {
      if (!boundaryName.trim()) return;
      processedGeoJsonData = {
        type: "FeatureCollection",
        features: [
          {
            type: "Feature",
            properties: {
              name: boundaryName,
            },
            geometry: geoJsonData as {
              type: "Polygon" | "MultiPolygon";
              coordinates: unknown;
            },
          },
        ],
      };
    } else {
      return;
    }

    try {
      await postBoundaryMutation.mutateAsync({
        body: {
          organization_id: organization.hasData() ? organization.data.id : "",
          feature_collection: processedGeoJsonData as {
            type: "FeatureCollection";
            features: {
              type: "Feature";
              geometry: {
                type: "Polygon" | "MultiPolygon";
                coordinates?: unknown;
              };
              properties: { [key: string]: unknown } | null;
            }[];
          },
          property_name:
            geoJsonType === "FeatureCollection" ? selectedNameKey : "name",
        },
      });

      toast({
        title: "Success",
        description: "Boundaries imported successfully",
        status: "success",
        duration: 3000,
      });

      onBoundaryImported();
      handleCancel();
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to import boundaries",
        status: "error",
        duration: 5000,
      });
    }
  };

  return (
    <ResizableDrawer
      isOpen={isOpen}
      onClose={handleCancel}
      header="Import boundaries"
      contentProps={{ bg: "white" }}
      footer={
        <HStack spacing={2} w="full">
          <Button
            variant="outline"
            flex={1}
            size="sm"
            h={8}
            onClick={handleCancel}
            borderColor="gray.300"
          >
            Cancel
          </Button>
          <Button
            colorScheme="primary"
            flex={1}
            size="sm"
            h={8}
            onClick={handleImport}
            isDisabled={
              !geoJsonData ||
              uploadError !== null ||
              (geoJsonType === "FeatureCollection" && !selectedNameKey) ||
              (geoJsonType === "Geometry" && !boundaryName.trim())
            }
            isLoading={postBoundaryMutation.isPending}
          >
            Import boundary
          </Button>
        </HStack>
      }
    >
      <VStack spacing={6} align="stretch">
        <FormControl>
          <FormLabel
            fontSize="sm"
            fontWeight="medium"
            color="gray.700"
            mb={1.5}
          >
            Shape
          </FormLabel>
          <VStack spacing={2} align="stretch">
            <Input
              ref={fileInputRef}
              type="file"
              accept=".geojson"
              onChange={handleFileUpload}
              display="none"
            />
            {uploadedFile && uploadStatus && (
              <UploadFileItem
                file={uploadedFile}
                progress={uploadStatus === "uploading" ? 50 : 100}
                status={uploadStatus}
                errorMessage={uploadError || undefined}
                onRemoveFile={handleRemoveFile}
              />
            )}
            <Button
              variant="outline"
              leftIcon={<RiUploadLine />}
              size="md"
              h={10}
              px={4}
              py={2.5}
              borderColor="rgba(0,0,0,0.16)"
              opacity={uploadedFile && uploadStatus === "success" ? 0.5 : 1}
              alignSelf="flex-start"
              onClick={handleUploadClick}
              disabled={!!(uploadedFile && uploadStatus === "success")}
            >
              Upload file
            </Button>
            <Text fontSize="xs" lineHeight="4">
              You can upload{" "}
              <Text as="span" fontWeight="bold">
                a single file
              </Text>{" "}
              containing a single feature or a feature collection, in the
              supported format: .geoJSON.{" "}
              <Link
                href="https://help.avela.org/hc/en-us/articles/40075093241101-Boundaries"
                color="purple.500"
                textDecoration="underline"
                isExternal
              >
                Learn more about boundaries
              </Link>
            </Text>
          </VStack>
        </FormControl>

        {geoJsonData &&
          uploadStatus === "success" &&
          geoJsonType === "FeatureCollection" && (
            <>
              <FormControl>
                <FormLabel
                  fontSize="sm"
                  fontWeight="medium"
                  color="gray.700"
                  mb={1.5}
                >
                  Select an identifying property:
                </FormLabel>
                <Select
                  value={selectedNameKey}
                  onChange={(e) => setSelectedNameKey(e.target.value)}
                  placeholder="Select property for feature names"
                  size="md"
                  bg="white"
                >
                  {availablePropertyKeys.map((key) => (
                    <option key={key} value={key}>
                      {key}
                    </option>
                  ))}
                </Select>
                <Text fontSize="xs" color="gray.500" mt={1.5}>
                  Select a property from the file to be used as a boundary name.
                  You'll be able to edit this later.
                </Text>
              </FormControl>

              <Box>
                <Text
                  fontSize="sm"
                  fontWeight="700"
                  color="gray.700"
                  mb={3}
                  fontFamily="Inter"
                >
                  ITEMS:{" "}
                  {(geoJsonData as GeoJSONFeatureCollection).features.length}
                </Text>
                <Box
                  border="1px solid"
                  borderColor="gray.200"
                  borderRadius="md"
                  bg="gray.50"
                >
                  <Box
                    px={4}
                    py={3}
                    borderBottom="1px solid"
                    borderColor="gray.200"
                    bg="white"
                  >
                    <Text fontSize="sm" color="gray.700" fontWeight="semibold">
                      {selectedNameKey || "Property"}
                    </Text>
                  </Box>
                  {generateFeatureNames(
                    geoJsonData
                      ? transformToFeatures(
                          geoJsonData,
                          geoJsonType || "FeatureCollection"
                        )
                      : [],
                    geoJsonType || "FeatureCollection",
                    selectedNameKey,
                    boundaryName
                  ).map((name, index) => (
                    <Box
                      key={index}
                      px={4}
                      py={3}
                      borderBottom="1px solid"
                      borderColor="gray.200"
                      _last={{ borderBottom: "none" }}
                      bg="white"
                    >
                      <Text fontSize="sm" color="gray.700" fontWeight="medium">
                        {name}
                      </Text>
                    </Box>
                  ))}
                </Box>
              </Box>
            </>
          )}

        {geoJsonData &&
          uploadStatus === "success" &&
          geoJsonType === "Feature" && (
            <FormControl>
              <FormLabel
                fontSize="sm"
                fontWeight="medium"
                color="gray.700"
                mb={1.5}
              >
                Boundary name:
              </FormLabel>
              <Input
                value={boundaryName}
                onChange={(e) => setBoundaryName(e.target.value)}
                placeholder="Enter boundary name"
                size="md"
                bg="white"
              />
              <Text fontSize="xs" color="gray.500" mt={1.5}>
                Name for the imported boundary feature.
              </Text>
            </FormControl>
          )}

        {geoJsonData &&
          uploadStatus === "success" &&
          geoJsonType === "Geometry" && (
            <FormControl>
              <FormLabel
                fontSize="sm"
                fontWeight="medium"
                color="gray.700"
                mb={1.5}
              >
                Boundary name:
              </FormLabel>
              <Input
                value={boundaryName}
                onChange={(e) => setBoundaryName(e.target.value)}
                placeholder="Enter boundary name"
                size="md"
                bg="white"
                required
              />
              <Text fontSize="xs" color="gray.500" mt={1.5}>
                Name for the imported boundary geometry.
              </Text>
            </FormControl>
          )}
      </VStack>
    </ResizableDrawer>
  );
};
